import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  InputBase,
  Paper,
  Fade,
  Popper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  Divider,
  Dialog,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  Assignment,
  Person,
  Contacts,
  MeetingRoom,
  Devices,
  Inventory2,
  LocalShipping,
  PlaylistAddCheck,
  AdminPanelSettings,
  AssignmentInd,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../../hooks/common/useDebounce';
import { useGlobalSearch } from '../../../hooks/api/useGlobalSearch';

// ── Styled components ────────────────────────────────────────────────────────

const SearchWrapper = styled(Box)(({ theme, focused }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${focused ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
  gap: theme.spacing(1),
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),
  ...(focused && {
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
  }),
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: theme.typography.body2.fontSize,
  '& .MuiInputBase-input': {
    padding: theme.spacing(0.75, 0),
    width: '100%',
  },
}));

const Dropdown = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  maxHeight: 480,
  overflowY: 'auto',
  border: `1px solid ${theme.palette.divider}`,
}));

// ── Config ───────────────────────────────────────────────────────────────────

const BUCKET_CONFIG = {
  tasks:           { labelKey: 'search.buckets.tasks',           icon: <Assignment fontSize="small" />,         route: (id) => `/tasks/${id}` },
  staff:           { labelKey: 'search.buckets.staff',           icon: <Person fontSize="small" />,             route: (id) => `/staff/${id}` },
  contacts:        { labelKey: 'search.buckets.contacts',        icon: <Contacts fontSize="small" />,           route: (id) => `/contacts/${id}` },
  rooms:           { labelKey: 'search.buckets.rooms',           icon: <MeetingRoom fontSize="small" />,        route: (id) => `/rooms/${id}` },
  equipment:       { labelKey: 'search.buckets.equipment',       icon: <Devices fontSize="small" />,            route: (id) => `/equipment/${id}` },
  inventory_items: { labelKey: 'search.buckets.inventory_items', icon: <Inventory2 fontSize="small" />,         route: (id) => `/inventory/items/${id}` },
  suppliers:       { labelKey: 'search.buckets.suppliers',       icon: <LocalShipping fontSize="small" />,      route: (id) => `/inventory/suppliers/${id}` },
  procedures:      { labelKey: 'search.buckets.procedures',      icon: <PlaylistAddCheck fontSize="small" />,   route: (id) => `/procedures/${id}` },
  roles:           { labelKey: 'search.buckets.roles',           icon: <AdminPanelSettings fontSize="small" />, route: (id) => `/roles/${id}` },
  delegations:     { labelKey: 'search.buckets.delegations',     icon: <AssignmentInd fontSize="small" />,      route: (id) => `/delegations/${id}` },
};

// ── History helpers ───────────────────────────────────────────────────────────

const HISTORY_KEY = 'orbit_search_history';
const MAX_HISTORY = 6;

const loadHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
};

const saveToHistory = (term) => {
  if (!term || term.trim().length < 2) return;
  const trimmed = term.trim();
  const prev = loadHistory().filter((h) => h !== trimmed);
  localStorage.setItem(HISTORY_KEY, JSON.stringify([trimmed, ...prev].slice(0, MAX_HISTORY)));
};

// ── Component ────────────────────────────────────────────────────────────────

export default function SearchBar() {
  const { t } = useTranslation(['common', 'delegations']);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState(loadHistory);
  const [anchorWidth, setAnchorWidth] = useState(150);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);

  const debouncedQuery = useDebounce(query, 280);
  const { buckets, totalCount, isLoading } = useGlobalSearch(debouncedQuery);

  const showingHistory = focused && query.length === 0 && recentSearches.length > 0;

  // Flat ordered list — history items when idle, search results when querying
  const flatItems = useMemo(() => {
    if (showingHistory) {
      return recentSearches.map((term) => ({ _isRecent: true, term }));
    }
    return Object.entries(BUCKET_CONFIG).flatMap(([key]) =>
      (buckets[key] ?? []).map((item) => ({ ...item, _bucket: key }))
    );
  }, [buckets, showingHistory, recentSearches]);

  // Reload history whenever the input gains focus
  useEffect(() => {
    if (focused) setRecentSearches(loadHistory());
  }, [focused]);

  // Desktop: drive Popper open state
  useEffect(() => {
    if (isMobile) return;
    const showResults = focused && (isLoading || debouncedQuery.length >= 2);
    const showHistory = focused && query.length === 0 && recentSearches.length > 0;
    setOpen(showResults || showHistory);
    setSelectedIndex(-1);
  }, [focused, isLoading, debouncedQuery, query, recentSearches.length, isMobile]);

  // Mobile dialog: auto-focus input and reset state on open/close
  useEffect(() => {
    if (mobileOpen) {
      setRecentSearches(loadHistory());
      setTimeout(() => mobileInputRef.current?.focus(), 80);
    } else {
      setQuery('');
      setFocused(false);
      setSelectedIndex(-1);
    }
  }, [mobileOpen]);

  const handleSelect = useCallback(
    (item) => {
      if (item._isRecent) {
        setQuery(item.term);
        setSelectedIndex(-1);
        (isMobile ? mobileInputRef : inputRef).current?.focus();
        return;
      }
      const config = BUCKET_CONFIG[item._bucket];
      if (config) {
        saveToHistory(query);
        setRecentSearches(loadHistory());
        navigate(config.route(item.id));
      }
      setQuery('');
      setOpen(false);
      setMobileOpen(false);
      (isMobile ? mobileInputRef : inputRef).current?.blur();
    },
    [navigate, query, isMobile]
  );

  const handleKeyDown = (e) => {
    const dropdownActive = isMobile
      ? showingHistory || isLoading || debouncedQuery.length >= 2
      : open;
    if (!dropdownActive) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(flatItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      setQuery('');
      setOpen(false);
      setMobileOpen(false);
    }
  };

  // Desktop: close when clicking outside
  useEffect(() => {
    if (isMobile) return;
    const onMouseDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isMobile]);

  // Track anchor width live so Popper follows CSS animation
  useEffect(() => {
    if (!anchorRef.current) return;
    const ro = new ResizeObserver(([entry]) => setAnchorWidth(entry.contentRect.width));
    ro.observe(anchorRef.current);
    return () => ro.disconnect();
  }, []);

  const populatedBuckets = Object.entries(BUCKET_CONFIG).filter(
    ([key]) => (buckets[key] ?? []).length > 0
  );

  // Shared results content — used in both Popper (desktop) and Dialog (mobile)
  const renderResultsContent = () => {
    let runningIndex = 0;
    return (
      <>
        {showingHistory && (
          <>
            <Typography
              variant="caption"
              sx={{
                px: 2, py: 0.5,
                display: 'flex', alignItems: 'center', gap: 0.75,
                color: 'text.secondary', fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                backgroundColor: 'action.hover',
              }}
            >
              <HistoryIcon sx={{ fontSize: 12 }} />
              {t('common:search.recent')}
            </Typography>
            <List dense disablePadding>
              {recentSearches.map((term, idx) => (
                <ListItemButton
                  key={`recent-${term}`}
                  selected={idx === selectedIndex}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect({ _isRecent: true, term });
                  }}
                  sx={{ py: 0.75, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    <HistoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={term}
                    slotProps={{ primary: { variant: 'body2', noWrap: true } }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {isLoading && totalCount === 0 && (
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">{t('common:search.searching')}</Typography>
          </Box>
        )}

        {!isLoading && totalCount === 0 && debouncedQuery.length >= 2 && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('common:search.no_results', { query: debouncedQuery })}
            </Typography>
          </Box>
        )}

        {populatedBuckets.map(([key, config], groupIdx) => {
          const items = buckets[key];
          const groupStart = runningIndex;
          runningIndex += items.length;

          return (
            <Box key={key}>
              {groupIdx > 0 && <Divider />}
              <Typography
                variant="caption"
                sx={{
                  px: 2, py: 0.5,
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  color: 'text.secondary', fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  backgroundColor: 'action.hover',
                }}
              >
                {React.cloneElement(config.icon, { sx: { fontSize: 12 } })}
                {t(`common:${config.labelKey}`)}
              </Typography>
              <List dense disablePadding>
                {items.map((item, itemIdx) => {
                  const flatIdx = groupStart + itemIdx;
                  const isHighlighted = flatIdx === selectedIndex;
                  return (
                    <ListItemButton
                      key={`${key}-${item.id}`}
                      selected={isHighlighted}
                      onMouseEnter={() => setSelectedIndex(flatIdx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect({ ...item, _bucket: key });
                      }}
                      sx={{ py: 0.75, px: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                        {config.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={
                          item.type === 'delegation' && item.subtitle
                            ? t(`delegations:statuses.${item.subtitle}`, item.subtitle)
                            : item.subtitle ?? undefined
                        }
                        slotProps={{
                          primary: { variant: 'body2', fontWeight: 500, noWrap: true },
                          secondary: { variant: 'caption', noWrap: true },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </>
    );
  };

  // ── Mobile mode ─────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <IconButton
          color="inherit"
          onClick={() => setMobileOpen(true)}
          size="small"
          aria-label="search"
        >
          <SearchIcon fontSize="small" />
        </IconButton>

        <Dialog
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          fullWidth
          maxWidth={false}
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              padding: 0,
            },
          }}
          slotProps={{
            paper: {
              sx: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                maxWidth: '100%',
                m: 0,
                borderRadius: 0,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                maxHeight: '80vh',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
            <InputBase
              inputRef={mobileInputRef}
              placeholder={t('common:search.placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              inputProps={{ 'aria-label': 'global search' }}
              sx={{ flex: 1, fontSize: theme.typography.body2.fontSize }}
            />
            {isLoading && <CircularProgress size={14} sx={{ flexShrink: 0, color: 'text.disabled' }} />}
            <IconButton size="small" onClick={() => setMobileOpen(false)} edge="end">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ overflowY: 'auto' }}>
            {renderResultsContent()}
          </Box>
        </Dialog>
      </>
    );
  }

  // ── Desktop mode ─────────────────────────────────────────────────────────────
  return (
    <Box
      ref={anchorRef}
      sx={{
        position: 'relative',
        width: 420,
        transition: 'width 0.25s ease',
      }}
    >
      <SearchWrapper focused={focused ? 1 : 0}>
        <SearchIcon
          fontSize="small"
          sx={{ color: focused ? 'primary.main' : 'text.disabled', flexShrink: 0 }}
        />
        <StyledInput
          inputRef={inputRef}
          placeholder={t('common:search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          inputProps={{ 'aria-label': 'global search' }}
        />
        {isLoading && <CircularProgress size={14} sx={{ flexShrink: 0, color: 'text.disabled' }} />}
      </SearchWrapper>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ width: anchorWidth, zIndex: 1400 }}
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Dropdown elevation={4}>
              {renderResultsContent()}
            </Dropdown>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}
